import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import LeadershipKpiCard from '../components/ContactCenter/LeadershipKpiCard';
import LeadershipPodTable from '../components/ContactCenter/LeadershipPodTable';
import LeadershipDailyChart from '../components/ContactCenter/LeadershipDailyChart';
import VolumeDrivers from '../components/ContactCenter/VolumeDrivers';
import CityVolume from '../components/ContactCenter/CityVolume';
import CityMap from '../components/ContactCenter/CityMap';
import { fmtAsOf, oldestAsOf, fmtCount, fmtMmSs, fmtMoney, fmtPct, STATUS } from '../components/ContactCenter/format';
import { btn, c } from '../theme';
import { CC_PAGE_MAX_WIDTH } from '../components/ContactCenter/pageWidth';

// Contact Center · Board — the old app's /board, for the support
// center's phone work: service level, Handled, AHT and speed of answer over a
// window, against the window before it and the year to date, by pod and by
// day. Every figure is computed on the server from stored Genesys data
// (services/ccLeadership.js); this page only draws it.

const DATA_FLOOR = '2026-06-04';

// Today's date in Pacific time, as YYYY-MM-DD. Formatted in the zone rather
// than via toISOString(), which reads the UTC date and, after 5 pm Pacific,
// returns tomorrow — the old Board's bug.
function pacificToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());
}

function lastMonth(today) {
  const [y, m] = today.split('-').map(Number);
  const start = new Date(Date.UTC(y, m - 2, 1));
  const end = new Date(Date.UTC(y, m - 1, 0));
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
}

export default function ContactCenterLeadership() {
  const today = pacificToday();
  const [mode, setMode] = useState('mtd');
  const [custom, setCustom] = useState({ start: '', end: '' });

  const params = mode === 'last-month' ? lastMonth(today)
    : mode === 'custom' && custom.start && custom.end ? custom
      : {};
  const q = useQuery({
    queryKey: ['cc-leadership', params.start || 'mtd', params.end || ''],
    queryFn: () => api.get('/contact-center/leadership', { params }),
    refetchInterval: 5 * 60 * 1000,
  });
  const d = q.data;

  const monthsOf = (key) => (d?.months || []).map((m) => ({ label: m.label, partialFrom: m.partialFrom, legacy: m.legacy, value: m[key] }));
  const fc = d?.kpis.handled.forecast;
  const asOf = oldestAsOf(d?.asOf);   // the older of the pulls: nothing is fresher than it

  return (
    <div style={s.page}>
      {/* The old Board's header is two rows: the name and "Data as of" across
          the top, then the range control on the left with the striped-bar
          legend against the right margin. The range control therefore sits
          BELOW PageHeader rather than in its actions slot — PageHeader is
          shared by fifteen pages and this arrangement is one page's. */}
      <PageHeader
        eyebrow="Contact Center"
        title="Athens Support Center Board Overview — Phone"
        subtitle={d?.range.prior ? `Compared with ${d.range.prior.label}` : 'Support center phone performance'}
        actions={asOf ? <span style={s.asOf}>Data as of {fmtAsOf(asOf)}</span> : null}
      />

      <div style={s.controlRow}>
        <div style={s.controls} role="group" aria-label="Date range">
          {d && <span style={s.rangeName}>{d.range.label}</span>}
          {[['mtd', 'MTD'], ['last-month', 'Last Month'], ['custom', 'Custom']].map(([key, label]) => (
            <button key={key} type="button" onClick={() => setMode(key)} aria-pressed={mode === key}
              style={mode === key ? s.pillOn : s.pill}>{label}</button>
          ))}
          {mode === 'custom' && (
            <>
              <input id="cc-lead-start" type="date" aria-label="Start date" min={DATA_FLOOR} max={today}
                value={custom.start} onChange={(e) => setCustom((v) => ({ ...v, start: e.target.value }))} style={s.date} />
              <input id="cc-lead-end" type="date" aria-label="End date" min={DATA_FLOOR} max={today}
                value={custom.end} onChange={(e) => setCustom((v) => ({ ...v, end: e.target.value }))} style={s.date} />
            </>
          )}
        </div>
        <p style={s.legend}>
          <span style={s.legendSwatch} aria-hidden="true" />
          Striped bars = data from our old phone system (InContact). We switched to Genesys on June 4, 2026.
        </p>
      </div>

      {q.isLoading && <LoadingSpinner />}
      {q.error && <div style={s.error}>Could not load the Leadership overview: {q.error.message}</div>}

      {d && (
        <>
          {d.range.clamped && (
            <div style={s.notice}>Contact Center data starts on June 4, 2026, when Athens moved to Genesys; the window starts there.</div>
          )}
          <div style={s.cards}>
            <LeadershipKpiCard
              title="Service level · 60 s"
              value={d.kpis.serviceLevel.value} status={STATUS.serviceLevel(d.kpis.serviceLevel.value)}
              target={`${d.targets.serviceLevelPct.toFixed(1)}%`}
              goal={d.targets.serviceLevelPct} goalUnit="pct" goalBetter="up"
              change={d.kpis.serviceLevel.change} changeUnit="pts" better="up" priorLabel={d.range.prior?.label}
              ytd={d.kpis.serviceLevel.ytd} months={monthsOf('serviceLevel')} fmt={fmtPct}
            />
            <LeadershipKpiCard
              title={`Call volume${mode === 'mtd' ? ' · MTD' : ''}`}
              target={fc?.forecast != null ? fmtCount(fc.forecast) : null} targetLabel="Forecast (Athens)"
              value={d.kpis.handled.value}
              goal={fc?.forecast ?? null} goalUnit="fcstPct" goalPct={fc?.vsPct ?? null} goalBetter="up"
              change={d.kpis.handled.change} changeUnit="%" better={null} priorLabel={d.range.prior?.label}
              ytd={d.kpis.handled.ytd} months={monthsOf('handled')} fmt={fmtCount}
              note={[
                d.kpis.handled.complete ? null : 'Some days in this window have no count stored yet.',
                fc?.vsPct != null && fc.coveragePct < 100
                  ? `Forecast covers ${fc.coveragePct}% of the days; compared on those days only.` : null,
              ].filter(Boolean).join(' ') || null}
            />
            <LeadershipKpiCard
              title="Average handle time"
              value={d.kpis.aht.value} status={STATUS.aht(d.kpis.aht.value)}
              target={fmtMmSs(d.targets.ahtSecs)}
              goal={d.targets.ahtSecs} goalUnit="secs" goalBetter="down"
              change={d.kpis.aht.change} changeUnit="s" better="down" priorLabel={d.range.prior?.label}
              ytd={d.kpis.aht.ytd} months={monthsOf('aht')} fmt={fmtMmSs}
            />
            <LeadershipKpiCard
              title="Cost per call*"
              value={d.kpis.costPerCall.value}
              target="—" targetLabel="Budget"
              goal={null}
              change={d.kpis.costPerCall.change} changeUnit="$" better="down" priorLabel={d.range.prior?.label}
              ytd={d.kpis.costPerCall.ytd} months={monthsOf('costPerCall')} fmt={fmtMoney}
              note="*Modeled from AHT and platform cost, not a Finance actual."
            />
            <LeadershipKpiCard
              title="Speed of answer"
              value={d.kpis.asa.value}
              change={d.kpis.asa.change} changeUnit="s" better="down" priorLabel={d.range.prior?.label}
              ytd={d.kpis.asa.ytd} months={monthsOf('asa')} fmt={fmtMmSs}
            />
          </div>
          <div style={s.split}>
            <VolumeDrivers drivers={d.drivers} other={d.driversOther} total={d.breakdownTotal} rangeLabel={d.range.label} />
            <CityVolume cities={d.cities} rangeLabel={d.range.label} />
          </div>
          {/* Full width under the table it extends: the table is the top ten
              with exact figures, the map is every city that took calls. */}
          {d.cityMap && <CityMap cityMap={d.cityMap} rangeLabel={d.range.label} />}
          <LeadershipPodTable pods={d.pods} allPods={d.allPods} rangeLabel={d.range.label} />
          <LeadershipDailyChart daily={d.daily} target={d.targets.serviceLevelPct} />
        </>
      )}
    </div>
  );
}

const s = {
  page:     { maxWidth: CC_PAGE_MAX_WIDTH, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '16px' },
  split:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '14px', alignItems: 'start' },
  cards:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' },
  controlRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' },
  controls: { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  rangeName: { fontSize: '12px', fontWeight: 600, color: c.textMuted, marginRight: '4px' },
  // Boxed, top right, as the old Board has it — a chip of plain text beside
  // the title read as part of the subtitle.
  asOf: { fontSize: '12px', color: c.textStrong, border: `1px solid ${c.border}`,
    borderRadius: '8px', padding: '5px 10px', whiteSpace: 'nowrap' },
  legendSwatch: {
    display: 'inline-block', width: '10px', height: '10px', marginRight: '6px',
    verticalAlign: '-1px', border: `1px solid ${c.borderStrong}`, borderRadius: '2px',
    background: `repeating-linear-gradient(135deg, ${c.borderStrong} 0 3px, ${c.surface} 3px 6px)`,
  },
  pill:     { ...btn.secondaryXs },
  pillOn:   { ...btn.secondaryXs, background: c.navy, color: '#fff', borderColor: c.navy },
  date:     { fontSize: '12px', padding: '4px 8px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontFamily: 'inherit', color: c.textStrong },
  chip:     { fontSize: '11px', color: c.textMuted },
  notice:   { fontSize: '12px', color: c.warningText, background: c.warningTint, padding: '8px 12px', borderRadius: '8px' },
  error:    { fontSize: '13px', color: c.dangerText },
  legend:   { fontSize: '11px', color: c.textMuted, margin: 0, textAlign: 'right' },
};
